// =============================================================================
// Library Asset Detail — preview, editable metadata, variants, versions, usage
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/use-debounce';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Combobox,
  Input,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import type { ComboboxOption } from '@tkpod/ui';
import type { LibraryAssetDetail } from '@tkpod/shared';
import { toast } from '@/lib/toast';
import {
  archiveAsset,
  assetDownloadUrl,
  completeAssetVersion,
  createTag,
  fetchAsset,
  fetchCategories,
  fetchTags,
  flattenCategories,
  libraryKeys,
  patchAsset,
  replaceAsset,
  searchAssets,
} from './api';
import { LibrarySingleUploadModal } from './LibrarySingleUploadModal';

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'READY') return 'success';
  if (status === 'PROCESSING') return 'warning';
  if (status === 'FAILED') return 'danger';
  return 'default';
}

export function LibraryAssetDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const assetQuery = useQuery({
    queryKey: libraryKeys.asset(id),
    queryFn: () => fetchAsset(id),
    enabled: !!id,
    // Auto-refresh while a (re)upload is processing so the new image + version
    // appear without a manual page refresh.
    refetchInterval: (query) => (query.state.data?.status === 'PROCESSING' ? 2000 : false),
  });
  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const tagsQuery = useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags });

  const asset = assetQuery.data;

  // Editable form state, seeded from the loaded asset.
  const [form, setForm] = useState<Partial<LibraryAssetDetail> | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  // Seed the editable form once per asset (not on every poll refetch, so a
  // background refresh during processing never clobbers in-progress edits).
  useEffect(() => {
    if (asset) setForm({
      sku: asset.sku,
      title: asset.title,
      description: asset.description,
      keywords: asset.keywords,
      colors: asset.colors,
      categoryId: asset.categoryId,
    });
  }, [asset?.id]);

  const categoryOptions = useMemo(
    () => flattenCategories(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const tagOptions = useMemo(() => (tagsQuery.data ?? []).map((t) => ({ value: t.id, label: t.name })), [tagsQuery.data]);

  /** Inline tag creation from the tag picker — refreshes the tag list. */
  const onCreateTag = async (name: string): Promise<ComboboxOption> => {
    try {
      const created = await createTag({ name });
      void queryClient.invalidateQueries({ queryKey: libraryKeys.tags });
      return { value: created.id, label: created.name };
    } catch (e) {
      toast((e as Error).message, 'error');
      throw e;
    }
  };

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => patchAsset(id, body),
    onSuccess: () => {
      toast('Saved', 'success');
      setSkuError(null);
      void queryClient.invalidateQueries({ queryKey: ['library'] });
    },
    onError: (e: Error) => {
      if (/sku/i.test(e.message)) setSkuError(e.message);
      else toast(e.message, 'error');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (opts: { withVariants?: boolean; force?: boolean }) => archiveAsset(id, opts),
    onSuccess: (res) => {
      toast(res.hardDeleted ? 'Deleted' : 'Archived', 'success');
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      navigate('/library');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  // Replace-image flow (hooks before any early return).
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  // Variant upload modal (batchless single-init with primaryAssetId preset).
  const [variantUploadOpen, setVariantUploadOpen] = useState(false);

  // --- variant linking picker (hooks MUST run before any early return) ------
  const [variantSearch, setVariantSearch] = useState('');
  const [variantLabel, setVariantLabel] = useState('');
  const dq = useDebounce(variantSearch, 300);
  const candidatesQuery = useQuery({
    queryKey: ['library', 'variant-candidates', dq],
    // Flat search so primaries are returned; caller filters self/nested out.
    queryFn: () => searchAssets(`q=${encodeURIComponent(dq)}&flat=true&pageSize=8`),
    enabled: dq.trim().length > 0,
  });

  if (assetQuery.isLoading || !form) {
    return <div className="flex justify-center py-24"><Spinner size="lg" label="Loading asset…" /></div>;
  }
  if (assetQuery.isError || !asset) {
    return <Card><p className="py-8 text-center text-sm text-red-400">Asset not found.</p></Card>;
  }

  const save = () => {
    const body: Record<string, unknown> = {
      title: form.title ?? null,
      description: form.description ?? null,
      keywords: form.keywords ?? [],
      colors: form.colors ?? [],
      categoryId: form.categoryId ?? null,
    };
    if (form.sku && form.sku !== asset.sku) body.sku = form.sku;
    saveMutation.mutate(body);
  };

  const setTags = (tagIds: string[]) => saveMutation.mutate({ tagIds });

  // Variant-linking candidates (hooks declared above the early returns).
  const candidates = (candidatesQuery.data?.items ?? []).filter(
    (c) => c.id !== id && !c.primaryAssetId,
  );

  const linkVariant = (primaryAssetId: string) =>
    saveMutation.mutate(
      { primaryAssetId, variantLabel: variantLabel.trim() || undefined },
      {
        onSuccess: () => {
          setVariantSearch('');
          setVariantLabel('');
        },
      },
    );
  const unlinkVariant = () => saveMutation.mutate({ primaryAssetId: null });

  /**
   * Fetch the signed URL and navigate an anchor to it. The URL is signed with
   * `Content-Disposition: attachment` server-side, so the browser saves the
   * file directly — no fetch()+blob and therefore no bucket CORS dependency.
   */
  const download = async (variant: 'working' | 'original', version?: number) => {
    try {
      const res = await assetDownloadUrl(id, variant, version);
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  /** Upload a replacement image as a new version (replace → PUT → complete). */
  const doReplace = async (file: File) => {
    setReplacing(true);
    try {
      const init = await replaceAsset(id);
      const put = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await completeAssetVersion(id, init.version);
      toast(`New version (v${init.version}) uploaded — processing`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['library'] });
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setReplacing(false);
    }
  };

  // A FAILED, never-processed asset is hard-deleted by the API (unusable: no
  // usage, no derivatives) — reflect that in the button/confirm copy.
  const isHardDelete = asset.status === 'FAILED' && asset.currentVersion === 1 && !asset.thumbUrl;

  const onArchive = async () => {
    const hasVariants = asset.variants.length > 0;
    const inUse = asset.usageCount > 0;
    const ok = await confirm({
      title: isHardDelete ? 'Delete asset' : 'Archive asset',
      description: isHardDelete
        ? 'This asset failed to process and cannot be used. It will be permanently deleted.'
        : (hasVariants ? `This asset has ${asset.variants.length} variant(s) that will be archived too. ` : '') +
          (inUse ? `It is used by ${asset.usageCount} design(s). ` : '') +
          'Continue?',
      confirmLabel: isHardDelete ? 'Delete' : 'Archive',
      tone: 'danger',
    });
    if (ok) archiveMutation.mutate({ withVariants: hasVariants, force: inUse });
  };

  return (
    <div className="space-y-6">
      {dialog}
      <Button variant="ghost" size="sm" onClick={() => navigate('/library')}>← Back to library</Button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Preview + versions */}
        <div className="space-y-4">
          <Card>
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
              {asset.largeUrl && <img src={asset.largeUrl} alt={asset.sku} className="max-h-full max-w-full object-contain" />}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(asset.status)} size="sm">{asset.status}</Badge>
              {!asset.galleryVisible && <Badge variant="warning" size="sm">hidden from gallery</Badge>}
              <span className="text-xs text-text-tertiary">{asset.widthPx}×{asset.heightPx} · {asset.mimeType} · v{asset.currentVersion}</span>
            </div>
            {asset.failureReason && <p className="mt-2 text-xs text-red-400">{asset.failureReason}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void download('working')}>Download</Button>
              <Button size="sm" variant="ghost" onClick={() => void download('original')}>Original</Button>
              <Button size="sm" loading={replacing} onClick={() => replaceInputRef.current?.click()}>
                Replace image
              </Button>
              <input
                ref={replaceInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void doReplace(f);
                  e.target.value = '';
                }}
              />
            </div>
            <p className="mt-1.5 text-xs text-text-tertiary">
              Uploads a new version. Older versions stay intact (saved designs are unaffected).
            </p>

            {/* Gallery visibility — applies immediately (not part of the Save
                form). Hiding a primary also hides its variants (the gallery
                lists primaries only). */}
            <div className="mt-3 flex items-center justify-between border-t border-brand-900 pt-3">
              <div>
                <p className="text-sm text-text-primary">Designer gallery</p>
                <p className="text-xs text-text-tertiary">
                  {asset.galleryVisible ? 'Visible to designers.' : 'Hidden from designers.'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate({ galleryVisible: !asset.galleryVisible })}
              >
                {asset.galleryVisible ? 'Hide from gallery' : 'Show in gallery'}
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader title="Version history" />
            <div className="mt-2 space-y-1">
              {asset.versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">v{v.version} · {(v.sizeBytes / 1024).toFixed(0)} KB</span>
                  <Button size="sm" variant="ghost" onClick={() => void download('working', v.version)}>Download</Button>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader title="Usage" description={`Used by ${asset.usageCount} design(s).`} />
          </Card>
        </div>

        {/* Editable metadata */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader title="Metadata" action={<Button size="sm" loading={saveMutation.isPending} onClick={save}>Save</Button>} />
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-text-secondary">SKU</label>
                <Input value={form.sku ?? ''} onChange={(e) => setForm({ ...form, sku: e.target.value })} error={skuError ?? undefined} />
              </div>
              <div>
                <label className="mb-1 block text-sm text-text-secondary">Category</label>
                <Combobox
                  options={categoryOptions}
                  value={form.categoryId ?? null}
                  onChange={(v) => setForm({ ...form, categoryId: v })}
                  placeholder="No category"
                  searchPlaceholder="Search categories…"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm text-text-secondary">Title</label>
                <Input value={form.title ?? ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm text-text-secondary">Description</label>
                <textarea
                  className="w-full rounded-md border border-brand-800 bg-surface-secondary px-3 py-2 text-sm text-text-primary"
                  rows={3}
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-text-secondary">Keywords (comma-separated)</label>
                <Input
                  value={(form.keywords ?? []).join(', ')}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-text-secondary">Colors (comma-separated)</label>
                <Input
                  value={(form.colors ?? []).join(', ')}
                  onChange={(e) => setForm({ ...form, colors: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                />
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Tags" />
            <div className="mt-3 flex flex-wrap gap-2">
              {asset.tags.map((t) => (
                <Badge key={t.id} variant="default" size="sm">
                  {t.name}
                  <button
                    type="button"
                    className="ml-1 text-text-tertiary hover:text-red-400"
                    onClick={() => setTags(asset.tags.filter((x) => x.id !== t.id).map((x) => x.id))}
                  >×</button>
                </Badge>
              ))}
            </div>
            <div className="mt-3 max-w-xs">
              {/* Action picker: value stays null so each pick appends a tag;
                  typing a new name offers inline creation. */}
              <Combobox
                options={tagOptions.filter((o) => !asset.tags.some((t) => t.id === o.value))}
                value={null}
                onChange={(v) => v && setTags([...asset.tags.map((t) => t.id), v])}
                placeholder="Add tag…"
                searchPlaceholder="Search or create tags…"
                onCreate={onCreateTag}
                allowClear={false}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Variants"
              description="Linked variants of this asset."
              action={
                !asset.primaryAssetId ? (
                  <Button size="sm" variant="outline" onClick={() => setVariantUploadOpen(true)}>
                    Upload variant
                  </Button>
                ) : undefined
              }
            />
            <div className="mt-3 flex flex-wrap gap-3">
              {asset.variants.length === 0 && <p className="text-sm text-text-tertiary">No variants linked.</p>}
              {asset.variants.map((v) => (
                <button key={v.id} type="button" onClick={() => navigate(`/library/assets/${v.id}`)} className="w-24 text-left">
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded border border-border bg-[#bdbdbd]">
                    {v.thumbUrl && <img src={v.thumbUrl} alt={v.sku} className="max-h-full max-w-full object-contain" />}
                  </div>
                  <p className="mt-1 truncate text-xs text-text-secondary">{v.variantLabel ?? v.sku}</p>
                </button>
              ))}
            </div>

            {/* Variant linking */}
            <div className="mt-4 border-t border-brand-900 pt-4">
              {asset.primaryAssetId ? (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => navigate(`/library/assets/${asset.primaryAssetId}`)}
                    className="text-sm text-brand-400 hover:underline"
                  >
                    This is a variant{asset.variantLabel ? ` (${asset.variantLabel})` : ''} — view primary
                  </button>
                  <Button size="sm" variant="ghost" onClick={unlinkVariant}>Unlink</Button>
                </div>
              ) : asset.variants.length > 0 ? (
                <p className="text-sm text-text-tertiary">This asset is a primary with variants.</p>
              ) : (
                <div>
                  <p className="mb-2 text-sm text-text-secondary">Link this asset as a variant of another asset:</p>
                  <div className="flex gap-2">
                    <Input placeholder="Search primary by SKU…" value={variantSearch} onChange={(e) => setVariantSearch(e.target.value)} />
                    <Input placeholder="Label (e.g. dark)" value={variantLabel} onChange={(e) => setVariantLabel(e.target.value)} className="max-w-[10rem]" />
                  </div>
                  {candidatesQuery.isFetching && (
                    <p className="mt-2 text-xs text-text-tertiary">Searching…</p>
                  )}
                  {candidates.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => linkVariant(c.id)}
                          className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-surface-tertiary"
                        >
                          {c.thumbUrl && <img src={c.thumbUrl} alt={c.sku} className="h-6 w-6 rounded object-cover" />}
                          <span className="text-text-primary">{c.sku}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Explicit empty state — a silent no-op made the search look broken */}
                  {dq.trim().length > 0 &&
                    !candidatesQuery.isFetching &&
                    candidates.length === 0 && (
                      <p className="mt-2 text-xs text-text-tertiary">
                        No linkable primary matches “{dq}” — assets that are already variants (or this asset itself) are excluded.
                      </p>
                    )}
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Danger zone" />
            <Button variant="danger" size="sm" className="mt-2" onClick={() => void onArchive()}>
              {isHardDelete ? 'Delete asset permanently' : 'Archive asset'}
            </Button>
            {isHardDelete && (
              <p className="mt-1.5 text-xs text-text-tertiary">
                This asset failed to process and cannot be used, so it is deleted permanently instead of archived.
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Variant upload — stays on this page; the new variant appears once processed */}
      <LibrarySingleUploadModal
        open={variantUploadOpen}
        onClose={() => setVariantUploadOpen(false)}
        primaryAssetId={asset.id}
        primarySku={asset.sku}
        onUploaded={() => {
          setVariantUploadOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['library'] });
        }}
      />
    </div>
  );
}
